import { Component, OnInit, OnDestroy } from '@angular/core';
import { PwaInstallService } from './pwa-install.service';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared/base/base.component';

@Component({
	selector: 'app-pwa-install-prompt',
	template: `
		<div class="pwa-install-banner" *ngIf="showInstallPrompt" role="alert" aria-label="Install C4 App">
			<div class="pwa-install-content">
				<div class="pwa-install-icon">
					<img src="assets/pwa-icons/icon-72x72.png" alt="" aria-hidden="true" />
				</div>
				<div class="pwa-install-text">
					<h3>Install C4 App</h3>
					<p *ngIf="!showIOSInstructions">Get quick access and enhanced experience</p>
					<p *ngIf="showIOSInstructions">Tap the <strong>Share</strong> button below, then select <strong>"Add to Home Screen"</strong></p>
				</div>
				<div class="pwa-install-actions">
					<button class="pwa-install-btn" *ngIf="!showIOSInstructions" (click)="onInstallClick()" aria-label="Install app">Install</button>
					<button class="pwa-dismiss-btn" (click)="onDismissClick()" [attr.aria-label]="showIOSInstructions ? 'Dismiss instructions' : 'Dismiss install prompt'">{{ showIOSInstructions ? 'Got it' : 'Not now' }}</button>
				</div>
			</div>
		</div>
	`,
	styles: [
		`
			.pwa-install-banner {
				position: fixed;
				bottom: 0;
				left: 0;
				right: 0;
				background-color: #103459;
				color: white;
				padding: 16px;
				box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
				z-index: 1000;
				animation: slideUp 0.3s ease-out;
			}

			@keyframes slideUp {
				from {
					transform: translateY(100%);
				}
				to {
					transform: translateY(0);
				}
			}

			.pwa-install-content {
				display: flex;
				align-items: center;
				max-width: 1200px;
				margin: 0 auto;
				gap: 16px;
			}

			.pwa-install-icon img {
				width: 48px;
				height: 48px;
				border-radius: 8px;
			}

			.pwa-install-text {
				flex: 1;
			}

			.pwa-install-text h3 {
				margin: 0 0 4px 0;
				font-size: 16px;
				font-weight: 600;
			}

			.pwa-install-text p {
				margin: 0;
				font-size: 14px;
				opacity: 0.9;
			}

			.ios-share-icon {
				width: 18px;
				height: 18px;
				vertical-align: middle;
				margin: 0 2px;
				filter: brightness(0) invert(1);
			}

			.pwa-install-actions {
				display: flex;
				gap: 8px;
			}

			.pwa-install-btn,
			.pwa-dismiss-btn {
				padding: 8px 16px;
				border: none;
				border-radius: 4px;
				font-size: 14px;
				font-weight: 500;
				cursor: pointer;
				transition: all 0.2s;
			}

			.pwa-install-btn {
				background-color: #ff8c42;
				color: white;
			}

			.pwa-install-btn:hover {
				background-color: #ff7a2e;
			}

			.pwa-dismiss-btn {
				background-color: transparent;
				color: white;
				border: 1px solid rgba(255, 255, 255, 0.3);
			}

			.pwa-dismiss-btn:hover {
				background-color: rgba(255, 255, 255, 0.1);
			}

			@media (max-width: 600px) {
				.pwa-install-content {
					flex-wrap: wrap;
				}

				.pwa-install-actions {
					width: 100%;
					flex-direction: column;
				}

				.pwa-install-btn,
				.pwa-dismiss-btn {
					width: 100%;
				}
			}
		`,
	],
})
export class PwaInstallPromptComponent extends BaseComponent implements OnInit, OnDestroy {
	showInstallPrompt = false;
	showIOSInstructions = false;
	private isIOS = false;

	constructor(private pwaInstallService: PwaInstallService) {
		super();
	}

	ngOnInit(): void {
		if (!this.pwaInstallService.isMobileDevice()) {
			return;
		}

		if (this.pwaInstallService.isInstalled()) {
			return;
		}

		if (this.pwaInstallService.wasDismissedThisSession()) {
			return;
		}

		this.isIOS = this.pwaInstallService.isIOS();

		if (this.isIOS) {
			// iOS doesn't fire beforeinstallprompt in any browser, show banner directly
			this.showInstallPrompt = true;
			return;
		}

		this.pwaInstallService
			.canInstall()
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe({
				next: (canInstall) => {
					this.showInstallPrompt = canInstall;
				},
				error: (err) => {
					console.error('PWA canInstall error:', err);
				},
			});
	}

	async onInstallClick(): Promise<void> {
		if (this.isIOS) {
			// iOS can't install programmatically, show manual instructions
			this.showIOSInstructions = true;
			return;
		}

		try {
			const result = await this.pwaInstallService.promptInstall();
			if (result === 'accepted') {
				this.pwaInstallService.clearDismissalFlag();
			} else if (result === 'dismissed') {
				this.pwaInstallService.markDismissedThisSession();
			}
		} catch {
			this.pwaInstallService.markDismissedThisSession();
		}
	}

	onDismissClick(): void {
		this.showInstallPrompt = false;
		this.pwaInstallService.dismissPrompt();
		this.pwaInstallService.markDismissedThisSession();
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
